import { Alert, Button, Card, Tabs } from "flowbite-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatToReal, isValidCNPJ, isValidCPF } from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { ADMIN, QUERIES } from "../../config/apiConfig";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiInformationCircle,
} from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import CardQueryDescription from "../common/CardQueryDescription";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import QueryInput from "../inputs/QueryInput";
import { useQuery } from "react-query";
import DrawerLoading from "../DrawerLoading";
import AcertaEssencialQuery from "./custom-queries-credit/AcertaEssencialQuery";
import LocalizacaoPFQuery from "./custom-queries-credit/LocalizacaoPFQuery";
import DefineRiscoQuery from "./custom-queries-credit/DefineRiscoQuery";
import LocalizacaoPJQuery from "./custom-queries-credit/LocalizacaoPJQuery";
import Protestos from "../common/query/Protestos";
import TitleStripe from "../common/TitleStripe";
import NoOcurrence from "../common/query/NoOcurrence";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import ScrBacenQuery from "./custom-queries-credit/ScrBacenQuery";
import { useReactToPrint } from "react-to-print";
import AvisoCard from "../common/query/AvisoCard";
import AcertaEssencialPositivoQuery from "./custom-queries-credit/AcertaEssencialPositivoQuery";
import PendenciasSerasaQuery from "./custom-queries-credit/PendenciasSerasaQuery";
import ScorePFQuery from "./custom-queries-credit/ScorePFQuery";
import ScorePJQuery from "./custom-queries-credit/ScorePJQuery";
import FooterLGPD from "../common/query/FooterLGPD";
import SPCSerasaPJ from "./custom-queries-credit/SPCSerasaPJ";
import PendenciasQuod from "./custom-queries-credit/PendenciasQuod";
import SPCSerasaPF from "./custom-queries-credit/SPCSerasaPF";
import DefineLimitePositivoQuery from "./DefineLimitePositivoQuery";
import DefineLimiteQuery from "./DefineLimiteQuery";
import CadinQuery from "./custom-queries-credit/CadinQuery";
import JudicialProcesses from "./custom-queries-credit/JudicialProcesses";
import DividasQuery from "./custom-queries-credit/DividasQuery";
import CrednetQuery from "./custom-queries-credit/CrednetQuery";
import SerasaPremiumQuery from "./custom-queries-credit/SerasaPremiumQuery";
import PendenciasSPCSerasaPF from "./custom-queries-credit/PendenciasSPCSerasaPF";
import CadastralPFQuery from "./custom-queries-cadastral/CadastralPFQuery";
import CadastralPJQuery from "./custom-queries-cadastral/CadastralPJQuery";
import SCPCQuery from "./custom-queries-credit/SCPCQuery";
import AnaliseDeCredito from "../common/query/AnaliseDeCredito";
import CreditoPreditivo from "./custom-queries-credit/CreditoPreditivo";
import ScoreRating from "./custom-queries-credit/ScoreRating";
import CustomCreditQueryAdditional from "./CustomCreditQueryAdditional";
import ConsolidatedByType, {
  collectBureauInstances,
  countConsolidatableSources,
  buildCadastroInternoInstances,
  BUREAU_SOURCE_KEYS,
  BESPOKE_SOURCE_KEYS,
} from "./custom-queries-credit/ConsolidatedByType";
import RendaPresumidaPF from "./custom-queries-credit/RendaPresumidaPF";
import GastoEstimadoPF from "./custom-queries-credit/GastoEstimadoPF";
import CadastralPremiumPFQuery from "./custom-queries-cadastral/CadastralPremiumPFQuery";
import CadastralPremiumPJQuery from "./custom-queries-cadastral/CadastralPremiumPJQuery";
import DefineNegocio from "./custom-queries-credit/DefineNegocio";
import AcertaCompletoPositivoQuery from "./custom-queries-credit/AcertaCompletoPositivoQuery";
import ScpcNetPFQuery from "./custom-queries-credit/ScpcNetPFQuery";
import SpcMixTopQuery from "./custom-queries-credit/SpcMixTopQuery";
import ConcentracaoRecebimentoPJQuery from "./custom-queries-credit/ConcentracaoRecebimentoPJQuery";
import ConcentracaoPagamentosPJQuery from "./custom-queries-credit/ConcentracaoPagamentosPJQuery";
import LiquidezPagamentoPJQuery from "../common/query/LiquidezPagamentoPJQuery";
import LiquidezRecebimentoPJQuery from "../common/query/LiquidezRecebimentoPJQuery";
import MediaDiasInadimplenciaPJQuery from "../common/query/MediaDiasInadimplenciaPJQuery";
import InformacoesFinanceirasPJQuery from "./custom-queries-credit/InformacoesFinanceirasPJQuery";
import CustomAlertWrapper from "./CustomAlertWrapper";
import SerasaBasicoQuery from "./custom-queries-credit/SerasaBasicoQuery";
import SerasaAvancadoPF from "./custom-queries-credit/SerasaAvancadoPF";
import { TbHandClick } from "react-icons/tb";
import DadosReceitaFederalPJ from "./custom-queries-cadastral/DadosReceitaFederalPJ";
import QuadroSocietarioPlusQuery from "./QuadroSocietarioPlusQuery";
import FaturamentoPresumidoPJQuery from "./FaturamentoPresumidoPJQuery";
import LimiteCreditoQuery from "./LimiteCreditoQuery";
import PagamentoFornecedoresQuery from "./PagamentoFornecedoresQuery";
import JudicialProcessesDetalhado from "./custom-queries-credit/JudicialProcessesDetalhado";
import RelatorioAvancadoPJQuery from "./RelatorioAvancadoPJQuery";
import PotencialPagamentoQuery from "./custom-queries-credit/PotencialPagamentoQuery";

/* Visão consolidada POR TIPO na aba "Ver tudo" — mesma regra da tela de
   consulta: agrupa cada tipo de informação numa seção só, com o rótulo de
   Fonte dizendo a procedência. */
const CONSOLIDAR_POR_TIPO = true;

const FINANCE_KEYS = new Set([
  "recebimentosViaBoletoPJ",
  "pagamentosViaBoletoPJ",
  "predicaoRecebimentoBoletoPJ",
  "concentracaoRecebimentoPJ",
  "concentracaoPagamentoPJ",
  "liquidezPagamentoPJ",
  "liquidezRecebimentoPJ",
  "mediaDiasInadimpleciaPJ",
  "recebimentosViaBoletoCartaoTedPJ",
  "pagamentosViaBoletoTedPJ",
  "scoreEvolucaoPJ",
]);

// ===================== TABS HELPERS =====================

// grupos por “fonte”
const SOURCE_SETS = {
  boavista: new Set([
    "defineRisco",
    "defineNegocioPositivo",
    "defineLimite",
    "defineLimitePositivo",
    "acertaEssencialPositivo",
    "acertaEssencial",
    "acertaCompleto",
  ]),
  serasa: new Set(["crednet", "serasa", "serasaPremium"]),
  spc: new Set([
    "spcMixTopPF",
    "spcMixTopPJ",
    "spcSerasaPF",
    "spcSerasaPJ",
    "pendSPCSerasaPF",
  ]),
  quod: new Set(["quod"]),
  scrBacen: new Set(["scrBacen"]),
  receitaFederalPJ: new Set(["receitaFederalPJ"]),
};

function coerceAdditionalList(additionalRaw) {
  if (!additionalRaw) return [];
  let add = additionalRaw;
  if (typeof add === "string") {
    try {
      const t = add.trim();
      add = t && t !== "null" && t !== "undefined" ? JSON.parse(t) : null;
    } catch {
      add = null;
    }
  }
  if (!add) return [];
  if (Array.isArray(add)) return add.filter(Boolean);
  if (typeof add === "object") return Object.values(add).filter(Boolean);
  return [];
}

function guessAdditionalKey(item = {}) {
  const tryKeys = [
    item.key,
    item.alias,
    item.nome,
    item.name,
    item.tipo,
    item.type,
  ];
  const first = tryKeys.find(Boolean);
  if (first) return String(first);
  if (item?.scrBacen) return "scrBacen";
  if (item?.consultaCredito?.scoreRating) return "scoreRating";
  return null;
}

function mapKeyToSource(key) {
  if (!key) return null;
  const k = String(key);
  for (const [source, set] of Object.entries(SOURCE_SETS)) {
    if (set.has(k)) return source;
  }
  if (/quod/i.test(k)) return "quod";
  if (/scrBacen/i.test(k)) return "scrBacen";
  if (/receitaFederalPJ/i.test(k)) return "receitaFederalPJ";
  if (/serasa|crednet/i.test(k)) return "serasa";
  if (/spc|protesto|cheque|scpc/i.test(k)) return "spc";
  if (/define|acerta/i.test(k)) return "boavista";
  return null;
}

function hasRestrictionsInResumo(resumo = {}) {
  const qty =
    Number(resumo?.pendenciasFinanceiras?.quantidadeTotal || 0) +
    Number(resumo?.protestos?.quantidadeTotal || 0) +
    Number(resumo?.chequesSemFundo?.quantidadeTotal || 0) +
    Number(resumo?.spc?.quantidadeTotal || 0) +
    Number(resumo?.serasa?.quantidadeTotal || 0) +
    Number(resumo?.acoesCiveis?.quantidadeTotal || 0);
  const val =
    Number(
      String(resumo?.pendenciasFinanceiras?.valorTotal || "0").replace(
        /[^\d]/g,
        "",
      ),
    ) +
    Number(String(resumo?.protestos?.valorTotal || "0").replace(/[^\d]/g, ""));
  return qty > 0 || val > 0;
}

function computeSourceRestricted(data, sourceKey) {
  const set = SOURCE_SETS[sourceKey];
  if (!set || !data) return false;
  for (const key of set) {
    const node = data?.[key];
    if (!node) continue;
    // formatos comuns
    if (node?.resumoConsulta && hasRestrictionsInResumo(node.resumoConsulta))
      return true;
    if (
      node?.consultaCredito?.resumoConsulta &&
      hasRestrictionsInResumo(node.consultaCredito.resumoConsulta)
    )
      return true;
    if (Array.isArray(node?.ocorrencias) && node.ocorrencias.length > 0)
      return true;
  }
  return false;
}

function hasRestrictionsAdditional(addList) {
  try {
    for (const it of addList) {
      const payload =
        it?.data != null
          ? it.data
          : it?.consultaCredito != null
            ? it.consultaCredito
            : it;
      if (
        payload?.resumoConsulta &&
        hasRestrictionsInResumo(payload.resumoConsulta)
      )
        return true;
      if (Array.isArray(payload?.ocorrencias) && payload.ocorrencias.length > 0)
        return true;
    }
  } catch {}
  return false;
}

function TabbedBodyReview({ data, document, renderComponentByKey }) {
  const additionalList = coerceAdditionalList(data?.additional);

  const hasBV =
    !!data && Array.from(SOURCE_SETS.boavista).some((k) => data?.[k] != null);
  const hasSerasa = !!data?.crednet || !!data?.serasa || !!data?.serasaPremium;
  const hasSPC =
    !!data?.spcMixTopPF ||
    !!data?.spcMixTopPJ ||
    !!data?.spcSerasaPF ||
    !!data?.spcSerasaPJ ||
    !!data?.pendSPCSerasaPF;
  const hasQuod = !!data?.quod;
  const hasReceita = !!data?.receitaFederalPJ;

  // ===== Helpers de restrição =====
  const hasJudicial = data?.acoesProcessos?.statusRetorno == 1;
  const judicialRestricted =
    (data?.acoesProcessos?.acoes?.processos?.length || 0) > 0;

  const hasCadin = data?.cadin?.statusRetorno == 1;
  const hasProtestoNacional = data?.protestoNacional?.statusRetorno == 1;
  const protestoRestricted =
    (data?.protestoNacional?.ocorrencias?.length || 0) > 0;

  const hasScr = data?.scrBacen?.statusRetorno == 1;

  function computeAllRestricted() {
    const src =
      computeSourceRestricted(data, "boavista") ||
      computeSourceRestricted(data, "serasa") ||
      computeSourceRestricted(data, "spc") ||
      computeSourceRestricted(data, "quod");
    const extras =
      judicialRestricted ||
      protestoRestricted ||
      hasRestrictionsAdditional(additionalList);
    return !!(src || extras);
  }

  // ===== Abas (inclui "Ver tudo") =====
  const tabs = [];
  tabs.push({
    key: "all",
    title: "Ver tudo",
    restricted: computeAllRestricted(),
  });

  if (hasBV)
    tabs.push({
      key: "boavista",
      title: "Fonte 1",
      restricted: computeSourceRestricted(data, "boavista"),
    });
  if (hasSerasa)
    tabs.push({
      key: "serasa",
      title: "Fonte 2",
      restricted: computeSourceRestricted(data, "serasa"),
    });
  if (hasSPC)
    tabs.push({
      key: "spc",
      title: "Fonte 3",
      restricted: computeSourceRestricted(data, "spc"),
    });
  if (hasQuod)
    tabs.push({
      key: "quod",
      title: "Fonte 4",
      restricted: computeSourceRestricted(data, "quod"),
    });

  if (hasJudicial) {
    tabs.push({
      key: "acoesProcessos",
      title: "Ações/Processos",
      restricted: judicialRestricted,
    });
  }
  if (hasCadin) {
    tabs.push({ key: "cadin", title: "CADIN", restricted: false });
  }
  if (hasProtestoNacional) {
    tabs.push({
      key: "protestoNacional",
      title: "Protesto Nacional",
      restricted: protestoRestricted,
    });
  }
  if (hasScr) {
    tabs.push({
      key: "scrBacen",
      title: "SCR/Bacen",
      restricted: false,
    });
  }
  if (hasReceita) {
    tabs.push({
      key: "receitaFederalPJ",
      title: "Receita Federal",
      restricted: false,
    });
  }
  if (additionalList.length > 0) {
    tabs.push({
      key: "additional",
      title: "Adicionais",
      restricted: hasRestrictionsAdditional(additionalList),
      count: additionalList.length,
    });
  }

  const [activeKey, setActiveKey] = useState(tabs[0]?.key || "");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!tabs.find((t) => t.key === activeKey)) {
      setActiveKey(tabs[0]?.key || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tabs)]);

  // ===== Render por fonte (já existia) =====
  const renderDataBySource = (source) => {
    const set = SOURCE_SETS[source];
    if (!set) return null;

    const base = Object.entries(data || {})
      .filter(([k, v]) => v && v.prioridade != null && set.has(String(k)))
      .map(([k, v]) => ({
        key: String(k),
        prioridade: Number(v.prioridade),
        dataItem: v,
      }));

    const extras = additionalList
      .map((it, idx) => {
        const guessedKey = guessAdditionalKey(it);
        const src = mapKeyToSource(guessedKey);
        if (src !== source) return null;

        const payload =
          it?.data != null
            ? it.data
            : it?.consultaCredito != null
              ? it.consultaCredito
              : it;

        const prio = Number(it?.prioridade ?? 9999 + idx);
        return {
          key: String(guessedKey || `additional_${idx + 1}`),
          prioridade: prio,
          dataItem: payload,
        };
      })
      .filter(Boolean);

    const merged = [...base, ...extras].sort(
      (a, b) => a.prioridade - b.prioridade,
    );
    if (!merged.length) return null;

    return (
      <>
        {merged.map((item, i) => (
          <React.Fragment key={`${source}_${item.key}_${i}`}>
            {renderComponentByKey(item.key, item.dataItem, document)}
          </React.Fragment>
        ))}
      </>
    );
  };

  // ===== Render "Ver tudo" (global por prioridade) =====
  /* Junta o top-level do data com os insumos que vieram em `additional`, para o
     consolidado achar as fontes independente de por onde elas chegaram. */
  const buildMergedBag = () => {
    const bag = { ...(data || {}) };
    additionalList.forEach((it, idx) => {
      const k = guessAdditionalKey(it) || `additional_${idx + 1}`;
      if (!(k in bag)) bag[k] = it;
    });
    return bag;
  };

  /* "Ver tudo" consolidado: o ConsolidatedByType agrupa por tipo tudo o que vem
     dos birôs, e o que sobra — insumos que não são de birô — segue empilhado por
     prioridade, como no "Ver tudo" simples. */
  const renderAllConsolidated = (mergedBag, instances) => {
    // Cadastrais internos cujo cadastro entrou na seção consolidada como
    // "Fonte interna": o componente deles renderiza no passthrough SEM o card
    // de dados cadastrais, para não repetir.
    const cadastroInternoHideKeys = new Set(
      buildCadastroInternoInstances(mergedBag).map((i) => i.key),
    );

    const ehConsolidado = (k) =>
      BUREAU_SOURCE_KEYS.has(String(k)) || BESPOKE_SOURCE_KEYS.has(String(k));

    const itemsArray = Object.entries(data)
      .filter(
        ([key, value]) =>
          value && value.prioridade != null && !ehConsolidado(key),
      )
      .map(([key, value]) => ({
        key,
        prioridade: Number(value.prioridade),
        dataItem: value,
      }));

    const extras = additionalList
      .map((it, idx) => {
        const guessedKey = guessAdditionalKey(it) || `additional_${idx + 1}`;
        if (ehConsolidado(guessedKey)) return null;
        const payload =
          it?.data != null
            ? it.data
            : it?.consultaCredito != null
              ? it.consultaCredito
              : it;
        const prio = Number(it?.prioridade ?? 9999 + idx);
        return { key: String(guessedKey), prioridade: prio, dataItem: payload };
      })
      .filter(Boolean);

    const merged = [...itemsArray, ...extras].sort(
      (a, b) => a.prioridade - b.prioridade,
    );

    /* O bloco de adicionais também não repete o que foi consolidado. */
    const additionalNaoConsolidado = additionalList.filter(
      (it, idx) =>
        !ehConsolidado(guessAdditionalKey(it) || `additional_${idx + 1}`),
    );

    const anyActiveFinance = Array.from(FINANCE_KEYS).some(
      (k) => data?.[k]?.statusRetorno == 1,
    );
    let financeRendered = false;

    return (
      <>
        <ConsolidatedByType
          instances={instances}
          mergedBag={mergedBag}
          renderComponentByKey={renderComponentByKey}
          document={document}
        />
        {merged.map(({ key, dataItem }, i) => {
          if (FINANCE_KEYS.has(key)) {
            if (financeRendered) return null;
            financeRendered = true;
            return anyActiveFinance ? (
              <InformacoesFinanceirasPJQuery
                key={`__finance___${i}`}
                dataFull={data}
              />
            ) : null;
          }
          return (
            <React.Fragment key={`cons_${key}_${i}`}>
              {renderComponentByKey(
                key,
                dataItem,
                document,
                cadastroInternoHideKeys.has(key)
                  ? { hideCadastro: true }
                  : undefined,
              )}
            </React.Fragment>
          );
        })}
        {additionalNaoConsolidado.length > 0 && (
          <div className="mt-2">
            <CustomCreditQueryAdditional data={additionalNaoConsolidado} />
          </div>
        )}
      </>
    );
  };

  const renderAll = () => {
    if (!data) return null;

    /* Visão consolidada por tipo: vale com 2+ fontes consolidáveis. Com uma
       fonte só não há o que comparar, e o "Ver tudo" segue empilhando os
       insumos na ordem de prioridade. */
    if (CONSOLIDAR_POR_TIPO) {
      const mergedBag = buildMergedBag();
      if (countConsolidatableSources(mergedBag) >= 2) {
        return renderAllConsolidated(
          mergedBag,
          collectBureauInstances(mergedBag),
        );
      }
    }

    // 1) pega todos os itens "principais" com prioridade
    const itemsArray = Object.entries(data)
      .filter(([_, value]) => value && value.prioridade != null)
      .map(([key, value]) => ({
        key,
        prioridade: Number(value.prioridade),
        dataItem: value,
      }));

    // 2) inclui os "additional" com prioridade (ou default)
    const extras = additionalList.map((it, idx) => {
      const guessedKey = guessAdditionalKey(it) || `additional_${idx + 1}`;
      const payload =
        it?.data != null
          ? it.data
          : it?.consultaCredito != null
            ? it.consultaCredito
            : it;
      const prio = Number(it?.prioridade ?? 9999 + idx);
      return { key: String(guessedKey), prioridade: prio, dataItem: payload };
    });

    const merged = [...itemsArray, ...extras].sort(
      (a, b) => a.prioridade - b.prioridade,
    );

    // 3) regra do bloco financeiro único
    const anyActiveFinance = Array.from(FINANCE_KEYS).some(
      (k) => data?.[k]?.statusRetorno == 1,
    );
    let financeRendered = false;

    return (
      <>
        {merged.map(({ key, dataItem }, i) => {
          if (FINANCE_KEYS.has(key)) {
            if (financeRendered) return null;
            financeRendered = true;
            return anyActiveFinance ? (
              <InformacoesFinanceirasPJQuery
                key={`__finance___${i}`}
                dataFull={data}
              />
            ) : null;
          }
          return (
            <React.Fragment key={`all_${key}_${i}`}>
              {renderComponentByKey(key, dataItem, document)}
            </React.Fragment>
          );
        })}
        {/* Caso queira garantir que o bloco de Adicionais apareça mesmo sem prioridade explícita */}
        {additionalList.length > 0 && (
          <div className="mt-2">
            <CustomCreditQueryAdditional data={data?.additional} />
          </div>
        )}
      </>
    );
  };

  if (!tabs.length) return null;

  const RestrictionIcon = ({ restricted }) =>
    restricted ? (
      <HiExclamationCircle
        className="ml-2 h-4 w-4 text-red-600"
        title="Com restrições"
        aria-label="Com restrições"
      />
    ) : (
      <HiCheckCircle
        className="ml-2 h-4 w-4 text-blue-600"
        title="Sem restrições"
        aria-label="Sem restrições"
      />
    );

  const CountBadge = ({ children }) => (
    <span className="ml-2 inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
      {children}
    </span>
  );

  return (
    <div className="mt-4">
      {/* BAR (sem setas) */}
      <div className="">
        <div className="">
          <div className="px-2 mb-1 flex items-center justify-between">
            <div className="font-semibold">
              <TbHandClick className="inline-block mr-1" /> Use as abas para
              alternar entre fontes.
            </div>
            <div className="text-gray-600 text-xs italic">
              Dica: “Ver tudo” mostra todos os blocos em ordem de prioridade.
            </div>
          </div>
          <div className="relative flex items-center">
            <div
              ref={scrollRef}
              role="tablist"
              aria-label="Resultados por fonte"
              className="hide-scrollbar -my-px flex w-full gap-2 overflow-x-auto px-2 py-2"
            >
              {tabs.map((t) => {
                const active = t.key === activeKey;
                return (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveKey(t.key)}
                    className={[
                      "group relative inline-flex items-center whitespace-nowrap rounded-xl px-3.5 py-2 text-sm transition",
                      active
                        ? "bg-slate-600 text-white shadow-sm"
                        : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <span className="font-semibold">{t.title}</span>
                    <RestrictionIcon restricted={t.restricted} />
                    {t.key === "additional" && t.count != null && (
                      <CountBadge>{t.count}</CountBadge>
                    )}
                    <span
                      className={[
                        "pointer-events-none absolute inset-x-2 -bottom-[2px] h-0.5 rounded-full",
                        active ? "bg-white/70" : "bg-transparent",
                      ].join(" ")}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100 animate-[fadeIn_200ms_ease-out]">
        <div className="-mt-6">
          {activeKey === "all" && renderAll()}
          {activeKey === "boavista" && renderDataBySource("boavista")}
          {activeKey === "serasa" && renderDataBySource("serasa")}
          {activeKey === "spc" && renderDataBySource("spc")}
          {activeKey === "quod" && renderDataBySource("quod")}
          {activeKey === "acoesProcessos" &&
            renderComponentByKey(
              "acoesProcessos",
              data?.acoesProcessos,
              document,
            )}
          {activeKey === "cadin" &&
            renderComponentByKey("cadin", data?.cadin, document)}
          {activeKey === "protestoNacional" &&
            renderComponentByKey(
              "protestoNacional",
              data?.protestoNacional,
              document,
            )}
          {activeKey === "scrBacen" &&
            renderComponentByKey("scrBacen", data?.scrBacen, document)}
          {activeKey === "receitaFederalPJ" &&
            renderComponentByKey(
              "receitaFederalPJ",
              data?.receitaFederalPJ,
              document,
            )}
          {activeKey === "additional" && (
            <div className="mt-2">
              <CustomCreditQueryAdditional data={data?.additional} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =================== COMPONENTE PRINCIPAL ===================
const CustomCreditQueryReview = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = true,
  isReviewOpen = false,
  hasPrintButton = false,
  // <— habilita abas aqui também
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorQuery, setErrorQuery] = useState("");
  const [showAlertsBoard, setShowAlertsBoard] = useState(false);
  const [hasTabs, setHasTabs] = useState(false);
  const [document, setDocument] = useState("");
  const [queryName, setQueryName] = useState("");
  const componentRef = useRef();
  const printRef = useRef();

  const reactToPrintFn = useReactToPrint({ contentRef: printRef });

  const {
    isLoading: isLoadingQueryReview,
    error: errorQueryReview,
    data: data,
  } = useQuery(
    ["data", query],
    async () => {
      const { data } = await axios.get(
        `${!isReviewOpen ? QUERIES.GET_USER_QUERY : QUERIES.REVIEW_QUERY}/${
          query?.id
        }`,
      );

      setShowAlertsBoard(data?.data?.qc?.showAlertsBoard == 0 ? false : true);
      setHasTabs(data?.data?.qc?.hasTabs == 1 ? true : false);
      setDocument(data.data.parameter);
      setQueryName(data?.data?.q?.name);

      return data?.data?.data;
    },
    { enabled: !!query },
  );

  const dataAlerts = useMemo(() => {
    if (!data) return null;
    const merged = { ...data };
    let add = data?.additional;

    if (typeof add === "string") {
      try {
        const t = add.trim();
        add = t && t !== "null" && t !== "undefined" ? JSON.parse(t) : null;
      } catch {
        add = null;
      }
    }
    if (!add) return merged;

    if (Array.isArray(add)) {
      add.forEach((item, idx) => {
        if (!item) return;
        const key =
          item?.key ||
          item?.alias ||
          item?.nome ||
          item?.name ||
          `additional_${idx + 1}`;
        merged[key] = item;
      });
    } else if (typeof add === "object") {
      Object.entries(add).forEach(([k, v]) => {
        if (v != null) merged[k] = v;
      });
    }

    return merged;
  }, [data]);

  const renderComponentByKey = (key, dataItem, document, opts = {}) => {
    /* `hideAgrupados`: usado só pela visão consolidada "Ver tudo" para renderizar
       um componente de fonte mostrando SOMENTE os blocos extras, escondendo o
       que já foi agrupado por tipo. */
    const hideAgrupados = !!opts.hideAgrupados;
    switch (key) {
      case "receitaFederalPJ":
        return <DadosReceitaFederalPJ data={dataItem} dataFull={data} />;
      case "potencialPagamento":
        return <PotencialPagamentoQuery data={dataItem?.consultaCredito} />;
      case "potencialPagamentoConsultaPF":
        return <PotencialPagamentoQuery data={dataItem?.consultaCredito} />;
      case "potencialPagamentoConsultaPJ":
        return <PotencialPagamentoQuery data={dataItem?.consultaCredito} />;
      case "cadastralCompleto":
        return (
          <CadastralPFQuery
            data={dataItem}
            dataFull={data}
            hideCadastro={!!opts.hideCadastro}
          />
        );
      case "cadastralCompletoPJ":
        return (
          <CadastralPJQuery
            data={dataItem}
            dataFull={data}
            hideCadastro={!!opts.hideCadastro}
          />
        );
      case "localizacao":
        return document?.length === 14 ? (
          <LocalizacaoPFQuery data={dataItem} />
        ) : (
          <LocalizacaoPJQuery data={dataItem} />
        );
      case "cadastralPremiumPF":
        return <CadastralPremiumPFQuery data={dataItem} dataFull={data} />;
      case "cadastralPremiumPJ":
        return <CadastralPremiumPJQuery data={dataItem} dataFull={data} />;
      case "acertaEssencial":
        return (
          <AcertaEssencialQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
            hideAgrupados={hideAgrupados}
          />
        );
      case "scpcNetPF":
        return (
          <ScpcNetPFQuery data={dataItem?.consultaCredito} dataFull={data} />
        );
      case "acertaEssencialPositivo":
        return (
          <AcertaEssencialPositivoQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
            hideAgrupados={hideAgrupados}
          />
        );
      case "acertaCompleto":
        return (
          <AcertaCompletoPositivoQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
            hideAgrupados={hideAgrupados}
          />
        );
      case "crednet":
        return (
          <CrednetQuery data={dataItem?.consultaCredito} dataFull={data} />
        );
      case "serasaRelatorioBasicoPF":
      case "serasaRelatorioBasicoPJ":
        return (
          <SerasaBasicoQuery data={dataItem.consultaCredito} dataFull={data} />
        );
      case "serasaRelatorioAvancadoPF":
        return (
          <SerasaAvancadoPF data={dataItem.consultaCredito} dataFull={data} />
        );
      case "serasaRelatorioAvancadoPJ":
        return <RelatorioAvancadoPJQuery data={dataItem} dataFull={data} />;
      case "serasaPremium":
        return <SerasaPremiumQuery data={dataItem.consultaCredito} />;
      case "defineRisco":
        return (
          <DefineRiscoQuery
            data={dataItem?.consultaCredito}
            dataFull={data}
            hideAgrupados={hideAgrupados}
          />
        );
      case "defineNegocioPositivo":
        return (
          <DefineNegocio
            data={dataItem?.consultaCredito}
            dataFull={data}
            hideAgrupados={hideAgrupados}
          />
        );
      case "defineLimite":
        return <DefineLimiteQuery data={dataItem} dataFull={data} />;
      case "defineLimitePositivo":
        return (
          <DefineLimitePositivoQuery
            data={dataItem}
            dataFull={data}
            hideAgrupados={hideAgrupados}
          />
        );
      case "spcSerasaPJ":
        return <SPCSerasaPJ data={dataItem} />;
      case "spcSerasaPF":
        return <SPCSerasaPF data={dataItem} />;
      case "pendSPCSerasaPF":
        return <PendenciasSPCSerasaPF data={dataItem} dataFull={data} />;
      case "spcMixTopPF":
        return <SpcMixTopQuery data={dataItem} />;
      case "spcMixTopPJ":
        return <SpcMixTopQuery data={dataItem} />;
      case "quadroSocietarioPlusPJ":
        return <QuadroSocietarioPlusQuery data={dataItem} />;
      case "faturamentoPresumidoPJ":
        return <FaturamentoPresumidoPJQuery data={dataItem} />;
      case "limiteCreditoPJ":
        return <LimiteCreditoQuery data={dataItem} />;
      case "pagamentoFornecedoresPJ":
        return <PagamentoFornecedoresQuery data={dataItem} />;
      case "serasa":
        return <PendenciasSerasaQuery data={dataItem} />;
      case "quod":
        return <PendenciasQuod data={dataItem} />;
      case "protestoNacional":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <>
                <TitleStripe className="mb-4">Protesto Nacional</TitleStripe>
                {dataItem?.ocorrencias?.length > 0 ? (
                  <Protestos
                    quantidadeTotal={dataItem?.ocorrencias?.length}
                    valorTotal={dataItem?.resumoConsulta?.protestos?.valorTotal}
                    protestos={dataItem.ocorrencias}
                    temVencimento={false}
                    isNacional={true}
                  />
                ) : (
                  <NoOcurrence />
                )}
              </>
            )}
          </div>
        );
      case "chequesSemFundo":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <>
                <TitleStripe>Cheques sem fundo</TitleStripe>
                {dataItem?.ocorrencias?.length > 0 ? (
                  <ChequesSemFundos chequesSemFundos={dataItem.ocorrencias} />
                ) : (
                  <NoOcurrence />
                )}
              </>
            )}
          </div>
        );
      case "scrBacen":
        return (
          <div>
            <ScrBacenQuery data={dataItem.scrBacen} />
          </div>
        );
      case "scorePF":
        return <ScorePFQuery data={dataItem} />;
      case "scorePJ":
        return <ScorePJQuery data={dataItem} />;
      case "cadin":
        return <CadinQuery data={dataItem} />;
      case "acoesProcessos":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <JudicialProcesses data={dataItem} />
            )}
          </div>
        );
      case "acoesProcessosDetalhado":
        return (
          <div>
            {dataItem?.statusRetorno == 1 && (
              <JudicialProcessesDetalhado data={dataItem} />
            )}
          </div>
        );
      case "scpcNet":
        return <SCPCQuery data={dataItem} />;
      case "dividas":
        return <DividasQuery data={dataItem} dataFull={data} />;
      case "creditoPreditivo":
        return <CreditoPreditivo data={dataItem} />;
      case "scoreRating":
        return <ScoreRating score={dataItem} data={data} />;
      case "rendaPresumidaPF":
        return <RendaPresumidaPF data={dataItem} />;
      case "gastoEstimadoPF":
        return <GastoEstimadoPF data={dataItem} />;
      case "concentracaoRecebimentoPJ":
        return <ConcentracaoRecebimentoPJQuery data={dataItem} />;
      case "concentracaoPagamentoPJ":
        return <ConcentracaoPagamentosPJQuery data={dataItem} />;
      case "liquidezPagamentoPJ":
        return <LiquidezPagamentoPJQuery data={dataItem} />;
      case "liquidezRecebimentoPJ":
        return <LiquidezRecebimentoPJQuery data={dataItem} />;
      case "mediaDiasInadimpleciaPJ":
        return <MediaDiasInadimplenciaPJQuery data={dataItem} />;
      default:
        return null;
    }
  };

  const renderSortedData = () => {
    if (!data) return null;

    const itemsArray = Object.entries(data)
      .filter(([_, value]) => value && value.prioridade != null)
      .sort(([, aValue], [, bValue]) => aValue.prioridade - bValue.prioridade);

    const anyActiveFinance = Array.from(FINANCE_KEYS).some(
      (k) => data?.[k]?.statusRetorno == 1,
    );

    let financeRendered = false;

    return itemsArray.map(([key, dataItem]) => {
      if (FINANCE_KEYS.has(key)) {
        if (financeRendered) return null;
        financeRendered = true;

        return anyActiveFinance ? (
          <InformacoesFinanceirasPJQuery key="__finance__" dataFull={data} />
        ) : null;
      }

      return (
        <React.Fragment key={key}>
          {renderComponentByKey(key, dataItem, document)}
        </React.Fragment>
      );
    });
  };

  return (
    <>
      <SliderToPreview />

      {errorQuery ? (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorQuery}</span>
        </Alert>
      ) : (
        <div
          className={`overflow-x-scroll print-content mobile-fit ${
            data || isLoading ? "block" : "hidden"
          }`}
          ref={(el) => {
            // apenas para impressão, se necessário
          }}
        >
          {!isLoading && data ? (
            <Card className="card-pdf p-5 relative overflow-hidden">
              <HeaderQuery
                hasHeader={hasHeaderPDF}
                title={queryName}
                date={data?.resumoRetorno?.dataConsulta}
                protocol={data?.resumoRetorno?.protocolo}
              />

              <div className="flex items-start gap-3 rounded-xl border border-yellow-300 bg-yellow-50/90 px-5 py-4">
                <div className="flex-1">
                  <p className="font-semibold text-yellow-900">Informativo</p>
                  <p className="mt-0.5 text-sm text-yellow-800 leading-snug">
                    Ressaltamos que a análise de crédito e seu resultado são de
                    responsabilidade exclusiva do cliente associado.
                  </p>
                </div>
              </div>

              {data?.IA && <CreditoPreditivo data={data?.IA} />}

              {showAlertsBoard && <CustomAlertWrapper data={data} />}

              {hasTabs ? (
                <TabbedBodyReview
                  data={data}
                  document={document}
                  renderComponentByKey={renderComponentByKey}
                />
              ) : (
                <>
                  {renderSortedData()}
                  {data?.additional && (
                    <CustomCreditQueryAdditional data={data?.additional} />
                  )}
                </>
              )}

              <FooterLGPD />
            </Card>
          ) : (
            <QueryLoading />
          )}
        </div>
      )}
    </>
  );
};

export default CustomCreditQueryReview;
